export type PolicyContent = {
    title: string;
    updatedLabel: string;
    body: string;
};

export const POLICY_VERSION = 'v1';

export const TERMS_OF_SERVICE_CONTENT: PolicyContent = {
    title: 'nibni Terms of Service',
    updatedLabel: 'Version v1',
    body: `Introduction

Welcome to nibni, a real estate operating software platform designed to support the full lifecycle of real estate operations through dedicated workspaces for all authorized users of the platform.

nibni provides digital infrastructure, operational coordination, administrative support, and workflow management across sales, reservations, ownership visibility, leasing, property management, investor participation, resale operations, reporting, and post-sale services.

Each user or organization operates within their own workspace, providing structured access to projects, teams, customer data, reservations, offers, reporting, documentation, and operational activities based on assigned roles and permissions.

By creating an account, accessing or using nibni, submitting information, uploading documents, or interacting with the platform in any manner, you acknowledge and agree to be bound by these Terms of Service.

These terms apply to all users of nibni and all activities conducted through the platform.

Definitions

User means any individual, company, organization, employee, administrator, buyer, investor, developer, partner, sales representative, manager, consultant, property manager, legal representative, finance representative, service provider, landlord, tenant, operator, or other authorized person accessing or using nibni.

Workspace means the dedicated operational environment assigned within nibni where users manage projects, teams, inventory, reservations, customer data, reporting, documentation, and operational activities.

Platform means the nibni website, software, mobile application, dashboards, systems, integrations, and related services.

Services means all operational, digital, administrative, reporting, support, and platform services provided by nibni.

Business Data means project information, inventory, customer records, documentation, reservations, pricing, reports, and all operational information uploaded to or generated through nibni.

Eligibility and Account Registration

Users must have the legal authority and capacity to enter into binding agreements and use nibni for legitimate business purposes.

If a user is registering or acting on behalf of a company, organization, or third party, the user confirms that they are authorized to act on behalf of that entity.

Users must provide accurate, complete, and current information during registration and must maintain such information throughout their use of the platform.

nibni reserves the right, at its sole discretion, to approve, reject, suspend, restrict, or terminate registrations, account access, or workspace access where reasonably necessary for operational, legal, commercial, regulatory, or security reasons.

Workspace Access and User Permissions

Each account is assigned to a workspace and access is granted based on assigned roles, permissions, and administrative controls.

Permissions may be customized by authorized workspace administrators and may include operational, commercial, financial, legal, reporting, sales, management, property, tenant, investor, or other access levels depending on business requirements.

Users are responsible for:
- maintaining account security
- protecting login credentials
- ensuring only authorized persons access the account
- managing internal access rights
- maintaining confidentiality of platform access
- ensuring appropriate use of assigned permissions

The account owner and workspace administrators remain fully responsible for all activity conducted under their workspace and assigned users.

nibni is not responsible for misuse resulting from permissions assigned by users, client organizations, or internal access decisions made by workspace administrators.

Scope of Services

nibni provides operational software, administrative coordination, and digital infrastructure supporting the full lifecycle of real estate operations, including but not limited to:
- project inventory management
- CRM and lead management
- sales operations
- offer generation
- reservations and unit holds
- ownership visibility
- investor participation workflows
- leasing and tenant coordination
- property management operations
- post-sale servicing
- maintenance coordination
- payment coordination
- reporting and analytics
- documentation management
- internal approvals and workflows
- partner and partner operations
- developer and project administration
- resale operations
- customer engagement and retention
- portfolio visibility and operational monitoring

Services may vary depending on the applicable project, workspace, contracting entity, and commercial relationship.

Responsibility Limitation

nibni provides operational software and administrative coordination services only.

nibni does not automatically assume the legal position of developer, landlord, seller, partner, legal lessor, escrow holder, payment guarantor, insurer, contractor, legal representative, financial intermediary, or property owner unless expressly agreed in writing under a separate executed agreement.

All legal, contractual, financial, ownership, leasing, regulatory, and transaction obligations remain solely with the relevant users and transaction parties.

Users remain solely responsible for conducting independent legal, financial, regulatory, tax, and commercial due diligence before entering into any transaction or commitment.

User Responsibilities

Users remain solely responsible for:
- accuracy of uploaded data
- completeness of records
- internal approvals
- reservation approvals
- pricing and commercial decisions
- contract approvals
- legal review processes
- financial decisions
- compliance with applicable laws and regulations
- authorization to upload third-party information
- obtaining required customer consents where applicable
- lease obligations where applicable
- property ownership obligations where applicable
- tenant obligations where applicable

All data entry must be completed by authorized users and internal teams responsible for operational processes.

nibni does not independently verify the legality, ownership, completeness, accuracy, or regulatory compliance of user-uploaded information.

Reporting accuracy, generated offers, reservations, and overall platform performance depend entirely on the accuracy and completeness of the information provided by users.

Reservations, Leasing and Transaction Limitations

nibni may facilitate:
- offer generation
- reservation workflows
- unit holds
- lead assignment
- leasing workflows
- tenant coordination
- ownership visibility
- sales coordination
- payment coordination
- internal documentation processes
- operational visibility across transactions

nibni does not guarantee:
- property availability
- unit pricing
- reservation approval
- lease approval
- contract execution
- final acceptance by transaction parties
- transfer of ownership
- completion of leasing or sale transactions
- maintenance outcomes
- third-party service provider performance

Final approvals, contracting, ownership transfer, leasing obligations, and execution of transactions remain solely the responsibility of the relevant users and transaction parties.

Subscription Fees, Billing and Payment Terms

Platform access and services may be subject to:
- implementation fees
- monthly subscriptions
- user-based billing
- workspace-based billing
- management fees
- service fees
- optional add-on services
- third-party service fees where applicable

All prices are exclusive of applicable taxes, including VAT or other local taxes where required.

Invoices may be issued in the agreed billing currency and payable in the applicable local currency based on the prevailing exchange rate or banking conditions on the date of payment, unless otherwise expressly agreed in writing.

Failure to settle invoices may result in restricted access, suspension of services, temporary account limitations, delayed operational support, or full workspace suspension.

Outstanding payment obligations remain enforceable after termination.

Confidentiality

All users agree to maintain the confidentiality of proprietary, operational, financial, technical, commercial, and customer information exchanged in connection with nibni.

Users may not disclose confidential information without appropriate authorization unless required by applicable law.

This confidentiality obligation survives suspension, termination, or closure of platform access.

Intellectual Property

All platform technology, workflows, branding, software, databases, systems, designs, reports, dashboards, documentation structures, and operational frameworks remain the exclusive property of nibni or its licensors where applicable.

Users may not copy, reproduce, resell, reverse engineer, republish, redistribute, misuse, or commercially exploit any part of the platform without prior written authorization.

Suspension, Restrictions and Termination

nibni reserves the right to suspend, restrict, or terminate access where reasonably necessary, including but not limited to:
- breach of these terms
- fraudulent activity
- misuse of the platform
- unauthorized access
- non-payment
- security concerns
- legal or regulatory risk
- misrepresentation of authority, ownership, or project rights
- actions that may harm platform integrity, operations, or commercial credibility

Users may request account termination through written notice subject to applicable commercial obligations and outstanding balances.

Termination does not remove payment obligations, confidentiality obligations, indemnification obligations, or liabilities arising from prior platform use.

How to Delete Your Account

You may delete your nibni account at any time through your Profile page.

To delete your account:
1. Log in to your nibni account
2. Navigate to your Profile page (/profile)
3. Scroll to the "Account Settings" section at the bottom of the page
4. Click "Delete Account"
5. Enter your password to confirm your identity
6. Review the implications of deletion
7. Confirm deletion to proceed

Before deleting your account, please note:
- All active reservations, holds, and pending offers may be cancelled
- Outstanding payment obligations remain enforceable after account deletion
- Business Data and operational records may be retained where required by law or for legitimate business purposes
- Workspace access and assigned permissions will be revoked
- You will lose access to all platform features and historical data

Account deletion is irreversible. If you wish to use nibni again in the future, you will need to create a new account.

For assistance with account deletion or if you have questions about data retention, please contact nibni support through the official communication channels available on the platform.

Limitation of Liability

To the maximum extent permitted by applicable law, nibni shall not be liable for:
- incorrect, incomplete, or misleading user-uploaded data
- failed reservations, leasing transactions, or sales outcomes
- partner, landlord, tenant, buyer, seller, or investor disputes
- legal disputes between transaction parties
- financial losses resulting from user decisions
- delays caused by incomplete records, approvals, or third-party providers
- external platform failures
- banking, financing, payment, or escrow failures
- governmental restrictions or legal changes affecting transactions
- security incidents outside nibni's reasonable control
- property damages or contractor failures
- business interruption caused by external service disruptions

Use of nibni does not replace independent legal, financial, tax, regulatory, or commercial due diligence by the user.

Indemnification

Users agree to indemnify, defend, and hold harmless nibni, its affiliates, directors, officers, employees, representatives, licensors, and service providers from and against any claims, liabilities, damages, losses, penalties, legal actions, costs, or expenses arising from:
- misuse of the platform
- inaccurate, incomplete, unlawful, or misleading uploaded information
- unauthorized actions by users or their teams
- lease disputes
- reservation disputes
- sales disputes
- investor disputes
- property ownership disputes
- regulatory violations caused by user conduct
- breach of these terms
- claims arising from transactions conducted outside nibni's direct operational responsibility

This protection survives suspension, termination, and closure of platform access.

Force Majeure

nibni shall not be liable for delays, failures, interruptions, or inability to perform obligations resulting from causes beyond its reasonable control, including but not limited to:
- government actions
- regulatory restrictions
- banking interruptions
- internet failures
- utility outages
- hosting failures
- third-party provider outages
- cyberattacks
- war
- civil unrest
- natural disasters
- public emergencies
- labor disruptions

Electronic Communications and Acceptance

By creating an account, using the platform, clicking acceptance buttons, submitting information, uploading documents, or continuing to use nibni, users consent to receive electronic communications including:
- account notifications
- verification messages
- operational notices
- billing communications
- policy updates
- security notices
- service announcements
- legal notices where applicable

Electronic acceptance, confirmations, clicks, and account actions may constitute legally binding consent where permitted by applicable law.

Amendments to Terms

nibni reserves the right to update, revise, amend, or modify these Terms of Service at any time.

Where reasonably required, users may be notified of material changes.

Continued use of the platform after such updates constitutes acceptance of the revised terms.

Governing Law and Jurisdiction

These terms shall be governed by and interpreted in accordance with the laws applicable to the contracting nibni entity providing the services to the user, unless otherwise expressly agreed in writing.

Where services are provided through nibni LLC or any local operating entity, the applicable governing law shall be the laws of the jurisdiction of that contracting entity.

Any dispute arising from or relating to these terms shall be subject to the exclusive jurisdiction of the competent courts or dispute resolution forum of the relevant contracting nibni entity, unless otherwise expressly agreed in writing.

Contact

For legal notices, billing matters, contractual matters, policy-related requests, or operational inquiries, users may contact nibni through the official communication channels made available on the platform or through the relevant contracting nibni entity providing the services.

nibni may update such communication methods from time to time without requiring amendment to these terms.

Acceptance

By creating an account, accessing, using the platform, uploading information, or continuing to use nibni after updates to these terms, the user confirms acceptance of these Terms of Service and acknowledges understanding of their rights, obligations, and responsibilities under the platform.`
};

export const PRIVACY_POLICY_CONTENT: PolicyContent = {
    title: 'nibni Privacy Policy',
    updatedLabel: 'Version v1',
    body: `Introduction

This Privacy Policy explains how nibni collects, uses, stores, processes, shares, protects, retains, and manages personal data, business information, and operational records in connection with the use of the nibni platform, including its website, mobile application, dashboards, workspaces, integrations, and related digital services.

nibni is a real estate operating software platform designed to support real estate operations through dedicated workspaces for all authorized users of the platform.

By creating an account, accessing or using nibni, submitting information, uploading documents, or interacting with the platform in any manner, you acknowledge and accept the practices described in this Privacy Policy.

This Privacy Policy applies to all users of nibni and all information processed through the platform.

Definitions

For the purposes of this Privacy Policy:

User means any individual, company, organization, employee, administrator, buyer, investor, developer, partner, sales representative, manager, consultant, property manager, legal representative, finance representative, service provider, or other authorized person accessing or using nibni.

Workspace means the dedicated operational environment assigned within nibni where users manage projects, teams, inventory, reservations, customer data, reporting, documentation, and operational activities.

Platform means the nibni website, software, mobile application, dashboards, systems, integrations, and related services.

Services means all operational, digital, administrative, reporting, support, and platform services provided by nibni.

Personal Data means any information capable of identifying an individual directly or indirectly.

Business Data means project information, inventory, customer records, documentation, reservations, pricing, reports, and all operational information uploaded to or generated through nibni.

Information We Collect

We may collect, process, store, review, retain, and make available the following categories of information where reasonably necessary for the operation of the platform:

Personal Information
- full name
- email address
- phone number
- nationality
- residential or business address
- identification documents where applicable
- verification documents
- passport or national identification details where required
- account credentials
- user preferences and settings
- signature records where applicable
- authentication and login records

Business and Operational Information
- company details
- project details
- property inventory
- unit availability
- pricing
- payment plans
- reservation details
- customer leads
- CRM records
- sales activity
- internal approvals
- contract-related information
- uploaded files and attachments
- property management information
- reporting data
- dashboard activity
- ownership visibility records
- transaction workflow records

Technical Information
- IP address
- device information
- browser type
- operating system
- login activity
- usage logs
- session data
- access timestamps
- security monitoring logs
- cookies and analytics information

Communication Information
- emails
- support requests
- customer service interactions
- notifications
- internal communication logs
- messaging platform integrations including WhatsApp where applicable

How Information May Be Used

We may process, use, disclose, transfer, and make available information where reasonably necessary for:
- account creation and management
- workspace administration
- reservation and offer workflows
- CRM and lead management
- inventory and project operations
- reporting and dashboard generation
- customer engagement and notifications
- identity verification and fraud prevention
- platform security and access control
- billing, invoicing, and subscription management
- customer support and account management
- internal audits and operational reviews
- performance monitoring and service improvements
- compliance with legal and regulatory obligations
- investigation of misuse, fraud, or unauthorized activity
- protection of legal rights and business interests of nibni and users
- commercial operations reasonably connected to platform services
- business continuity and platform improvements

Any sharing, disclosure, or transfer of information shall be limited to what is reasonably necessary for legitimate operational, contractual, legal, regulatory, or commercial purposes.

nibni may process information where there is a lawful basis to do so and in accordance with applicable law.

Legal Basis for Processing

We may process information where necessary for:
- performance of contractual obligations
- legitimate business operations
- compliance with legal and regulatory obligations
- fraud prevention and platform security
- enforcement of platform terms and agreements
- protection of rights, property, and legal interests
- user consent where applicable
- internal operational and audit requirements

Users remain solely responsible for ensuring they have lawful authority to upload third-party information into the platform.

Data Ownership and User Responsibility

Users remain solely responsible for:
- the legality of uploaded data
- accuracy of information provided
- completeness of records
- internal approvals
- authorization to upload third-party data
- obtaining customer consents where required
- compliance with local laws and regulatory obligations

nibni acts as the platform provider and processor of such information and does not independently verify the legality, ownership, accuracy, completeness, or regulatory compliance of user-uploaded information.

Users confirm that they have obtained all necessary permissions, consents, approvals, and legal rights required to upload, process, store, and manage such data through the platform.

Users agree to indemnify and hold harmless nibni against claims arising from unauthorized, inaccurate, unlawful, misleading, incomplete, or improperly obtained data.

Sharing of Information

Information may be shared, disclosed, or made available where reasonably necessary with:
- authorized workspace users
- internal users assigned by workspace administrators
- service providers supporting platform operations
- payment and billing providers
- cloud hosting providers
- CRM and communication providers
- identity verification providers
- technical support providers
- legal advisors, auditors, and compliance professionals
- governmental, judicial, and regulatory authorities where legally required
- commercial counterparties where necessary for platform operations and transaction workflows

Such sharing shall be limited to what is reasonably necessary and subject to applicable legal, contractual, and operational requirements.

Third-Party Services and Integrations

nibni may integrate with or rely on third-party services including but not limited to:
- CRM systems
- cloud storage providers
- payment providers
- identity verification systems
- messaging platforms
- WhatsApp integrations
- email providers
- analytics tools
- hosting providers
- API integrations
- signature providers
- banking or payment infrastructure providers

nibni is not responsible for privacy practices, outages, failures, delays, security incidents, or operational disruptions caused by third-party providers outside nibni's reasonable control.

Use of integrated services may be subject to separate third-party terms and policies.

Data Security

nibni implements commercially reasonable administrative, operational, and technical safeguards intended to protect data against unauthorized access, misuse, disclosure, loss, alteration, or destruction.

However, no internet transmission, hosting environment, platform, or storage system can guarantee absolute security or uninterrupted protection.

Users remain responsible for:
- password security
- internal access management
- authorized use of workspaces
- secure handling of their own devices and credentials

nibni shall not be liable for security failures resulting from user negligence, unauthorized credential sharing, or misuse of workspace access.

Data Retention

Information may be retained for as long as reasonably necessary to:
- provide services
- maintain operational continuity
- support audits and investigations
- enforce agreements and payment obligations
- resolve disputes
- comply with legal and regulatory obligations
- protect legal rights and commercial interests

Following account suspension or termination, nibni may retain certain records where legally required, operationally necessary, contractually relevant, or needed to protect legal rights.

Deletion of an account does not guarantee immediate deletion of all records where retention obligations apply.

International Data Processing

Where applicable, data may be processed, stored, or transferred across jurisdictions depending on service providers, hosting infrastructure, integrations, and operational requirements.

By using nibni, users acknowledge and consent to such processing where reasonably necessary for service delivery.

nibni shall take commercially reasonable steps to maintain appropriate safeguards where such transfers occur.

Cookies, Analytics and Tracking

nibni may use cookies, analytics tools, session tracking, and similar technologies for purposes including:
- platform functionality
- session management
- security monitoring
- fraud prevention
- performance optimization
- service improvements
- user behavior analysis
- reporting and operational analytics
- communication effectiveness monitoring

Users may control certain cookie settings through browser preferences where applicable.

User Rights

Subject to applicable law, users may request:
- access to personal information
- correction of inaccurate information
- restriction of certain processing
- account updates
- withdrawal of consent where applicable
- deletion requests where legally permitted

Certain legal, contractual, operational, regulatory, audit, and compliance obligations may limit immediate deletion, restriction, or modification of records.

nibni may retain records where reasonably necessary to protect legal rights and compliance obligations.

Compliance with Laws

Users remain solely responsible for ensuring compliance with all applicable laws and regulations related to:
- real estate transactions
- customer onboarding
- identity verification
- KYC requirements
- investor verification
- tax obligations
- licensing requirements
- financial regulations
- data protection obligations
- anti-money laundering obligations
- regulatory approvals including where applicable FRA requirements
- local jurisdictional compliance requirements

nibni does not replace legal, regulatory, compliance, tax, or financial advice.

Electronic Communications and Consent

By creating an account, using the platform, clicking acceptance buttons, submitting information, or continuing to use nibni, users consent to receive electronic communications including:
- account notifications
- verification messages
- operational notices
- billing communications
- policy updates
- security notices
- service announcements
- legal notices where applicable

Electronic acceptance, confirmations, clicks, and account actions may constitute legally binding consent where permitted by applicable law.

Limitation of Liability

nibni provides operational software and administrative support services only.

To the maximum extent permitted by applicable law, nibni shall not be liable for:
- incorrect or incomplete user-uploaded data
- unauthorized access caused by user negligence
- third-party provider failures
- regulatory actions arising from user conduct
- legal disputes between transaction parties
- financial losses resulting from user decisions
- delays caused by incomplete records or internal approvals
- governmental restrictions or legal changes affecting transactions
- security incidents outside nibni's reasonable control
- business interruption caused by external service failures

Users remain solely responsible for independent legal, financial, commercial, and regulatory due diligence.

Force Majeure

nibni shall not be liable for delays, failures, interruptions, or inability to perform obligations resulting from causes beyond its reasonable control, including but not limited to:
- government actions
- regulatory restrictions
- banking interruptions
- internet failures
- utility outages
- hosting failures
- third-party provider outages
- cyberattacks
- war
- civil unrest
- natural disasters
- public emergencies
- labor disruptions

Changes to This Privacy Policy

nibni reserves the right to update, revise, amend, or modify this Privacy Policy at any time.

Where reasonably required, users may be notified of material changes.

Continued use of the platform after such updates constitutes acceptance of the revised Privacy Policy.

Governing Law and Jurisdiction

This Privacy Policy shall be governed by and interpreted in accordance with the laws applicable to the contracting nibni entity providing the services to the user, unless otherwise expressly agreed in writing.

Where services are provided through nibni LLC or any local operating entity, the applicable governing law shall be the laws of the jurisdiction of that contracting entity.

Any dispute arising from or relating to this Privacy Policy shall be subject to the exclusive jurisdiction of the competent courts or dispute resolution forum of the relevant contracting nibni entity, unless otherwise expressly agreed in writing.

Contact

For legal notices, privacy requests, billing inquiries, or policy-related matters, users may contact nibni through the official communication channels made available on the platform or through the relevant contracting nibni entity providing the services.

nibni may update such communication methods from time to time without requiring amendment to this Privacy Policy.

Acceptance

By creating an account, accessing, using the platform, uploading information, or continuing to use nibni after updates to this Privacy Policy, the user confirms acceptance of this Privacy Policy and acknowledges understanding of how information may be collected, processed, stored, protected, retained, and managed.`
};

export const DELETE_ACCOUNT_CONTENT: PolicyContent = {
    title: 'Delete Your nibni Account',
    updatedLabel: 'Version v1',
    body: `Account Deletion

You may delete your nibni account at any time. This page explains the process, implications, and alternatives to permanent account deletion.

Before You Delete

Please consider the following before proceeding with account deletion:

Active Reservations and Holds
- Any active unit reservations or holds will be cancelled
- Reserved units will be released back to availability
- Pending offers may be withdrawn

Outstanding Obligations
- All unpaid invoices and subscription fees remain due
- Payment obligations survive account deletion
- Outstanding balances must be settled before deletion can be processed

Data and Records
- Your personal profile information will be removed
- Business Data uploaded to workspaces may be retained where required
- Operational records may be kept for legal, regulatory, or audit purposes
- Transaction history and reservation records may be preserved

Workspace Access
- All workspace memberships will be terminated
- Assigned roles and permissions will be revoked
- You will lose access to all platform features

How to Delete Your Account

Method 1: Through Profile Settings
1. Log in to your nibni account
2. Navigate to your Profile page
3. Scroll to the Account Settings section at the bottom
4. Click "Delete Account"
5. Enter your password to confirm your identity
6. Review the implications of deletion
7. Confirm deletion to proceed

Method 2: Through This Page
1. Ensure you are logged in to your nibni account
2. Click the "Delete My Account" button below
3. Enter your password to confirm
4. Review and confirm the deletion

What Happens After Deletion

Immediate Effects:
- Your account is marked for deletion
- Login access is immediately revoked
- Active sessions are terminated
- API tokens are invalidated

Within 30 Days:
- Personal data is purged from active systems
- Profile information is removed
- Contact preferences are deleted

Data Retention:
- Business records may be retained as required by law
- Financial records are kept per accounting regulations
- Audit logs are preserved for security purposes
- Anonymized usage data may be retained for analytics

Recovery Period

Once you confirm account deletion, there is no recovery period. The action is irreversible.

If you change your mind before confirming, simply close the deletion dialog.

If you wish to use nibni again after deletion, you will need to:
- Create a new account with a different email address
- Complete the registration and verification process
- Re-establish workspace memberships and permissions

Alternatives to Deletion

If you are experiencing issues or no longer wish to use the platform actively, consider these alternatives:

Temporary Deactivation
- Contact support to discuss temporary account suspension
- Your data is preserved and can be restored
- Useful for extended absences or business pauses

Update Contact Preferences
- Adjust notification settings to reduce communications
- Manage email preferences in your profile
- Control which alerts you receive

Workspace Management
- Leave workspaces you no longer need access to
- Transfer ownership to another team member
- Reduce your platform footprint without deleting your account

Export Your Data

Before deleting your account, you may want to export your data:
- Profile information can be viewed in your Profile page
- Reservation history is available in your Reservations dashboard
- Contact support for assistance with data export requests

Contact Support

If you have questions about account deletion, data retention, or need assistance:
- Use the support channels available within the platform
- Contact nibni through official communication methods
- Reference this page when discussing deletion-related inquiries

Legal Notice

Account deletion is subject to applicable terms of service, privacy policy, and legal obligations. nibni reserves the right to retain certain records where required by law, regulation, or legitimate business interests.

By proceeding with account deletion, you acknowledge and accept the implications described on this page.`
};

export function legalPolicyBySlug(slug: string | null | undefined): PolicyContent {
    const key = String(slug || '').trim().toLowerCase();
    if (key === 'privacy') return PRIVACY_POLICY_CONTENT;
    if (key === 'delete-account') return DELETE_ACCOUNT_CONTENT;
    return TERMS_OF_SERVICE_CONTENT;
}
